<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useTicketsStore } from 'dashboard/stores/tickets';
import { useTicketColumnsStore } from 'dashboard/stores/ticketColumns';
import TicketDetailDialog from './TicketDetailDialog.vue';

const props = defineProps({
  contactId: { type: [Number, String], required: true },
});

const PRIORITY_ICON = {
  urgent: 'i-woot-priority-urgent',
  high: 'i-woot-priority-high',
  medium: 'i-woot-priority-medium',
  low: 'i-woot-priority-low',
};

const { t } = useI18n();
const store = useStore();
const ticketsStore = useTicketsStore();
const columnsStore = useTicketColumnsStore();

const ticketDetailRef = ref(null);

const columns = computed(() => columnsStore.getColumns);
const contactTickets = computed(() =>
  ticketsStore.getTicketsList
    .filter(ticket => ticket.contact_id === Number(props.contactId))
    .sort((a, b) => b.id - a.id)
);

const columnFor = ticket =>
  columns.value.find(c => c.id === ticket.ticket_column_id);

const openTicket = ticket => ticketDetailRef.value?.open(ticket);

onMounted(() => {
  store.dispatch('agents/get');
  columnsStore.get();
  ticketsStore.get();
});
</script>

<template>
  <div class="flex flex-col gap-2 p-4">
    <ul v-if="contactTickets.length" class="flex flex-col gap-2">
      <li
        v-for="ticket in contactTickets"
        :key="ticket.id"
        class="flex flex-col gap-1 p-2.5 rounded-lg cursor-pointer bg-n-solid-1 outline outline-1 -outline-offset-1 outline-n-weak hover:outline-n-slate-6"
        @click="openTicket(ticket)"
      >
        <div class="flex items-center gap-1.5">
          <span class="text-xs shrink-0 text-n-slate-10 tabular-nums">#{{ ticket.display_id }}</span>
          <span class="flex-1 text-sm truncate text-n-slate-12">{{ ticket.title }}</span>
          <Icon
            v-if="ticket.priority"
            :icon="PRIORITY_ICON[ticket.priority]"
            class="size-4 shrink-0"
          />
        </div>
        <div class="flex items-center justify-between gap-2">
          <span class="inline-flex items-center gap-1 text-xs text-n-slate-11">
            <span
              class="w-2 h-2 rounded-full shrink-0"
              :style="{ backgroundColor: columnFor(ticket)?.color || '#94a3b8' }"
            />
            {{ columnFor(ticket)?.name }}
          </span>
          <Avatar
            v-if="ticket.assignee"
            :name="ticket.assignee.name"
            :src="ticket.assignee.avatar_url"
            :size="18"
            rounded-full
          />
        </div>
      </li>
    </ul>
    <p v-else class="p-4 text-sm text-center text-n-slate-11">
      {{ t('TICKETS.CONTACT_PANEL.EMPTY') }}
    </p>

    <TicketDetailDialog ref="ticketDetailRef" />
  </div>
</template>
